import { environment } from 'src/environments/environment';

/**
 * Gate da página de design system: ela é uma ferramenta de desenvolvimento e só
 * pode existir em ambiente de teste LOCAL.
 *
 * São três travas independentes (basta uma falhar para a página não existir):
 *   1. `environment.designSystemPage` — só é `true` em `environment.ts` (o arquivo
 *      usado por `ionic serve`); prod/green/blue/beta declaram `false`.
 *   2. `!environment.production`.
 *   3. hostname local — protege o caso de alguém publicar um build de dev.
 *
 * Sem as três, a rota nem chega a ser registrada no `AppRoutingModule`, então o
 * chunk lazy da página nunca é baixado.
 */

export const DESIGN_SYSTEM_PATH = 'design-system';

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'];

/** Faixas privadas: `ionic serve --external` publica em 192.168.x.x para testar no celular. */
const PRIVATE_RANGES = [/^10\./, /^192\.168\./, /^172\.(1[6-9]|2\d|3[01])\./];

export function isLocalHostname(hostname: string): boolean {
    if (!hostname) return false;

    const host = hostname.toLowerCase();

    if (LOCAL_HOSTNAMES.indexOf(host) !== -1) return true;
    if (host.endsWith('.local') || host.endsWith('.localhost')) return true;

    return PRIVATE_RANGES.some((range) => range.test(host));
}

/** `true` quando a URL atual é a da página de design system (`/design-system[/...]`). */
export function isDesignSystemUrl(pathname?: string): boolean {
    const path = pathname !== undefined
        ? pathname
        : (typeof window !== 'undefined' ? window.location.pathname : '');

    if (!path) return false;

    const normalized = path.toLowerCase();

    return normalized === `/${DESIGN_SYSTEM_PATH}` || normalized.startsWith(`/${DESIGN_SYSTEM_PATH}/`);
}

/** `true` só em ambiente de teste local — ver as três travas no topo do arquivo. */
export function designSystemPageEnabled(): boolean {
    if (environment.production) return false;
    if (!(environment as any).designSystemPage) return false;

    return typeof window !== 'undefined' && isLocalHostname(window.location.hostname);
}

/**
 * `true` quando o boot deve pular a carga do restaurante (a página de design system
 * não pertence a nenhum restaurante e roda sem backend).
 */
export function isDesignSystemBoot(): boolean {
    return designSystemPageEnabled() && isDesignSystemUrl();
}
